/**
 * One `medpdb2` row read as a ProductRecord.
 *
 * The row is the wire shape: a stored (masked) key, and the vendor offers spread over
 * fifteen numbered column families. Both stop here: the key is decoded with fromMasked,
 * and the families are folded into the slot list the domain speaks.
 */
import {
  productSelect,
  type SlotFamily,
  VENDOR_SLOT_COUNT,
} from '../../../shared/data/product-columns';
import { fromMasked } from '../shared/masked-ean';
import type { ProductRecord, UnitPrice, VendorSlot } from './product-record';

const SLOT_NUMBERS: readonly number[] = Array.from({ length: VENDOR_SLOT_COUNT }, (_, i) => i + 1);

const DISPLAY_COLUMNS: readonly string[] = [
  'EAN',
  'name',
  'brand',
  'category',
  'altcategory',
  'img',
  'page',
  'rating',
  'ratecount',
  'rating_rank',
  'size',
  'retail',
  'volprice_1',
  'volunit_1',
  'usp1',
  'usp2',
  'usp3',
  'desc',
  'contents',
  'dos',
  'reviews',
];

const SLOT_FAMILIES: readonly SlotFamily[] = [
  'apoid',
  'price',
  'price_promo_percent',
  'availability',
];

/** Every column the product-record reads select: the display columns, then the slot families. */
export const PRODUCT_RECORD_COLUMNS: string = productSelect(DISPLAY_COLUMNS, SLOT_FAMILIES);

/** One row as far as PostgREST guarantees its shape: an object, members unknown. */
export type ProductRecordRow = Readonly<Record<string, unknown>>;

/**
 * The record a row stands for, or null when its key is not a stored EAN, which the caller
 * counts. An unmarked key is a contract violation, never a code that is already real.
 */
export function rowToProductRecord(row: ProductRecordRow): ProductRecord | null {
  const ean = typeof row['EAN'] === 'string' ? fromMasked(row['EAN']) : null;
  if (!ean) return null;
  return {
    ean,
    name: asText(row['name']),
    brand: asText(row['brand']),
    category: asText(row['category']),
    altCategory: asText(row['altcategory']),
    image: asText(row['img']),
    page: asText(row['page']),
    rating: asNumber(row['rating']),
    ratingCount: asNumber(row['ratecount']),
    ratingRank: asNumber(row['rating_rank']),
    size: asText(row['size']),
    retail: asText(row['retail']),
    unitPrice: unitPriceOf(row),
    usps: [row['usp1'], row['usp2'], row['usp3']]
      .map(asText)
      .filter((usp): usp is string => usp !== null),
    description: asText(row['desc']),
    contents: asText(row['contents']),
    dosage: asText(row['dos']),
    reviews: asText(row['reviews']),
    slots: slotsOf(row),
  };
}

function slotsOf(row: ProductRecordRow): readonly VendorSlot[] {
  const slots: VendorSlot[] = [];
  for (const n of SLOT_NUMBERS) {
    const vendor = asText(row[`apoid_${n}`])?.toLowerCase();
    if (!vendor) continue;
    slots.push({
      vendor,
      price: asPrice(row[`price_${n}`]),
      promoPercent: asNumber(row[`price_promo_percent_${n}`]),
      availability: asText(row[`availability_${n}`]),
    });
  }
  return slots;
}

function unitPriceOf(row: ProductRecordRow): UnitPrice | null {
  const value = asPrice(row['volprice_1']);
  const unit = asText(row['volunit_1']);
  return value !== null && unit !== null ? { value, unit } : null;
}

/** The trimmed text a column holds, or null when it is empty or not text. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** The finite number a column holds, in numeric or Swedish decimal text form, or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** A price is a positive amount; zero and below is the scraper's "no price". */
function asPrice(value: unknown): number | null {
  const parsed = asNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}
